/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbstractFormComponent, BuilderNavigationService, isJobEditable } from '@gh/builder-shared';
import { AelEvents, BuilderPermission, getAelEventsArray } from '@gh/builder-shared-data';
import { TimerEvent } from '@gh/common-internal-shared';
import {
  ActionFlow,
  ContainerState,
  DEFAULT_AUTOSAVE_INTERVALS,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getRouteInitData,
  getRouteParentTitle,
  ImmutablePageState,
  isRouteForCreationMode,
  PAGE_STATE,
  queryDirty,
  queryFormMode,
  State,
  UserContext,
} from '@gh/common-shared';
import { TranslationTupleOrId } from '@gh/core-mls';
import { FormMode } from '@gh/core-ui';
import { getLocalValue, setLocalValue } from '@gh/core-util';
import { Observable } from 'rxjs';
import { JobCsDetailsMasterInfo } from '../../resources/job-cs-details.model';
import { JobCsDetailsCommandService, JobCsDetailsSaveFlowState } from './job-cs-details-command.service';
import { jobCsDetailsReducer } from './job-cs-details.reducers';
import {
  JOB_CS_DETAILS_INIT_STATE,
  JobCsDetailsState,
  queryJobCsId,
  queryJobCsMasterInfo,
} from './job-cs-details.state';

export function jobCsDetailsStateFactory(): State<JobCsDetailsState> {
  return new State(JOB_CS_DETAILS_INIT_STATE, jobCsDetailsReducer);
}

export function jobCsDetailsFormFactory(state: State<JobCsDetailsState>): ImmutablePageState<JobCsDetailsState> {
  return new ImmutablePageState(state, {
    formMode: queryFormMode,
    dirty: queryDirty,
  });
}

const JOB_CS_DETAILS_AUTOSAVE_LOCAL_KEY = 'job-cs-details:autosave';

@Component({
  selector: 'gh-job-cs-details',
  template: require('./job-cs-details.component.html'),
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    JobCsDetailsCommandService,
    { provide: State, useFactory: jobCsDetailsStateFactory },
    { provide: PAGE_STATE, useFactory: jobCsDetailsFormFactory, deps: [State] },
  ],
})
export class JobCsDetailsComponent extends AbstractFormComponent<JobCsDetailsState> implements OnInit {
  autosaveIntervals = DEFAULT_AUTOSAVE_INTERVALS;
  currentAutosaveInterval = getLocalValue(JOB_CS_DETAILS_AUTOSAVE_LOCAL_KEY, '30m');

  masterInfo$: Observable<FormStateSnapshot<JobCsDetailsMasterInfo>>;
  mode: FormMode;
  constructor(private commands: JobCsDetailsCommandService,
              private state: State<JobCsDetailsState>,
              @Inject(PAGE_STATE) public form: ContainerState<JobCsDetailsState>,
              private route: ActivatedRoute,
              private userContext: UserContext,
              private navigationService: BuilderNavigationService) {
    super();
  }

  get completeFlow(): ActionFlow<JobCsDetailsSaveFlowState> {
    return this.commands.completeFlow();
  }

  get parentTitle(): TranslationTupleOrId {
    return getRouteParentTitle(this.route) || 'lbl_jobs';
  }

  get jobNumber(): string {
    return getFormStateSnapshotValue(this.state.project(queryJobCsMasterInfo)).jobNumber;
  }

  ngOnInit(): void {
    const { init } = getRouteInitData(this.route);

    this.mode = isRouteForCreationMode(this.route)
      ? FormMode.CreateMode
      : this.userContext.hasPermission(BuilderPermission.JobAdmin.JobColorSelection.CREATE_EDIT) && isJobEditable(init)
        ? FormMode.EditMode
        : FormMode.ViewMode;

    this.masterInfo$ = this.state.select(queryJobCsMasterInfo);

    this.commands.load(init, this.mode);
  }

  onMasterInfoChange(state: FormStateSnapshot<JobCsDetailsMasterInfo>): void {
    this.commands.setMasterInfo(state);
  }

  goToAel(): void {
    this.navigationService.goToAel({
      label: 'lbl_job_color_selection',
      entityType: 'JOB',
      entityId: this.state.project(queryJobCsId),
      eventTypeIds: getAelEventsArray(AelEvents.Job.ColorSelections),
    });
  }

  onReset(): void {
    this.commands.reset();
  }

  onReplace(): void {
    this.commands.replace();
  }

  onOpenDocument(): void {
    this.commands.openDocument();
  }

  onPreview(): void {
    this.commands.preview();
  }

  onProduce(): void {
    this.commands.produce(
      {
        jobId: this.state.value.jobId,
        masterId: this.state.value.csMasterId,
        mode: this.mode,
      });
  }

  onAutoSave(event: TimerEvent): void {
    event.waitFor(this.commands.autoSave());
  }

  onChangeAutosaveInterval(interval: string): void {
    setLocalValue(JOB_CS_DETAILS_AUTOSAVE_LOCAL_KEY, interval);
  }
}
